import moment from "moment";

import { validateRequiredFieldsSkillsFeedback, validateRequiredFieldsCandidateRate } from 'utils/formValidations';

import FeedbackDialogWrapper from "components/Shared/FeedbackDialog";
import { CANDIDATE_REJECTION_REASONS_ARRAY, CANDIDATE_RATING_STATUSES_ARRAY, CANDIDATE_RATING_STATUSES } from "constants/candidateConstants";
import { DATE_FORMAT_WITH_DOTS } from "constants/commonConstants";
import { BULGARIAN_LABEL } from "constants/languages";

export const CHECKLIST_STEPS_KEYS = {
    SOFT_SKILLS_STEP: 'softSkillsDialog',
    HARD_SKILLS_STEP: 'hardSkillsDialog',
    CANDIDATE_RATED_STEP: 'candidateRatedDialog',
};

export const HIRING_CHECKLIST_TITLES = {
    SOFT_SKILLS_VETTED: 'Soft Skills Vetted',
    HARD_SKILLS_VETTED: 'Hard Skills Vetted',
    CANDIDATE_RATED: 'Candidate Rated',
    PROFILE_SENT_TO_CLIENT: 'Profile Sent to Client',
    APPLICATIONS_UPDATED: 'Applications Updated',
};

export const CHECKLIST_TITLE_TO_PROPERTY_MAP = {
    [HIRING_CHECKLIST_TITLES.SOFT_SKILLS_VETTED]: 'softSkillsStep',
    [HIRING_CHECKLIST_TITLES.HARD_SKILLS_VETTED]: 'hardSkillsStep',
    [HIRING_CHECKLIST_TITLES.CANDIDATE_RATED]: 'sendFeedbackToCandidateStep',
    [HIRING_CHECKLIST_TITLES.PROFILE_SENT_TO_CLIENT]: 'sentProfileToClientStep',
    [HIRING_CHECKLIST_TITLES.APPLICATIONS_UPDATED]: 'updateApplicationStatusStep',
};

export const CHECKLIST_TITLE_TO_DIALOG_MAP = {
    [HIRING_CHECKLIST_TITLES.SOFT_SKILLS_VETTED]: CHECKLIST_STEPS_KEYS.SOFT_SKILLS_STEP,
    [HIRING_CHECKLIST_TITLES.HARD_SKILLS_VETTED]: CHECKLIST_STEPS_KEYS.HARD_SKILLS_STEP,
    [HIRING_CHECKLIST_TITLES.CANDIDATE_RATED]: CHECKLIST_STEPS_KEYS.CANDIDATE_RATED_STEP,
};

export const HIRING_CHECKLIST_STEPS_ORDER = [
    'softSkillsStep',
    'hardSkillsStep',
    'sendFeedbackToCandidateStep',
    'sentProfileToClientStep',
    'updateApplicationStatusStep'
];

export const DIALOG_KEYS_TO_TITLES_MAP = (isEditMode, intl) => ({
    softSkillsDialog: `${isEditMode ? intl.formatMessage({ id: "edit" }) : intl.formatMessage({ id: "add-button-text" })} ${intl.formatMessage({ id: "soft-skills-feedback" })}`,
    hardSkillsDialog: `${isEditMode ? intl.formatMessage({ id: "edit" }) : intl.formatMessage({ id: "add-button-text" })} ${intl.formatMessage({ id: "hard-skills-feedback" })}`,
    candidateRatedDialog: intl.formatMessage({ id: "rate-candidate" }),
});

export const DIALOG_INFO_SECTION_CONTENT = (selectedLanguage) => {
    if (selectedLanguage === BULGARIAN_LABEL) {
        return {
            softSkillsDialog: 'Напишете обратна връзка от видеото на кандидата или интервюто за набиране на персонал. Съсредоточете се върху неща като комуникация, език, личност.',
            hardSkillsDialog: 'Напишете обратна връзка от проверката на личностните умения на кандидата. Фокусирайте се върху неща като технологичен стек, инструменти, известни процеси и концепции.',
            candidateRatedDialog: 'Обратната връзка, която предоставите в следното поле, ще бъде изпратена на кандидата. Моля, дайте конструктивни и изчерпателни коментари.',
        }
    } else {
        return {
            softSkillsDialog: 'Write feedback from the candidate video or recruitment interview. Focus on things such as communication, language, personality.',
            hardSkillsDialog: 'Write feedback from the candidate hard skills vetting. Focus on things such as tech stack, tools, known processes and concepts.',
            candidateRatedDialog: 'The feedback you provide in the following field will be sent to the candidate. Please provide constructive and comprehensive comments.',
        }
    }
};

const CANDIDATE_MISSING = (selectedLanguage) => selectedLanguage === BULGARIAN_LABEL ? 'Този кандидат не се е явил на интервю' : 'This candidate didn\'t show up for the interview';

export const STEP_VALIDATION_FUNCTION = {
    softSkillsDialog: (values) => validateRequiredFieldsSkillsFeedback(values),
    hardSkillsDialog: (values) => validateRequiredFieldsSkillsFeedback(values),
    candidateRatedDialog: (values) => validateRequiredFieldsCandidateRate(values),
};

export const CHECKLIST_DIALOGS_MAP = (selectedLanguage, intl) => ({
    softSkillsDialog: (props) => (
        <FeedbackDialogWrapper {...props}>
            <FeedbackDialogWrapper.FeedbackDialogInfoSection content={DIALOG_INFO_SECTION_CONTENT(selectedLanguage)[props.dialogKey]} />
            <FeedbackDialogWrapper.CheckboxItem name='candidateMissing' label={intl.formatMessage({ id: "candidate-did-not-show-up" })} size='medium' />
            <FeedbackDialogWrapper.FeedbackDescriptionSection id='description' name='description' label={intl.formatMessage({ id: "description" })} />
            <FeedbackDialogWrapper.RatingCriteriaWrapper>
                <FeedbackDialogWrapper.RatingCriteriaItem id='languages' name='languages' title={intl.formatMessage({ id: "languages" })} />
                <FeedbackDialogWrapper.RatingCriteriaItem id='communication' name='communication' title={intl.formatMessage({ id: "communication" })} />
                <FeedbackDialogWrapper.RatingCriteriaItem id='personality' name='personality' title={intl.formatMessage({ id: "personality" })} />
            </FeedbackDialogWrapper.RatingCriteriaWrapper>
        </FeedbackDialogWrapper>
    ),
    hardSkillsDialog: (props) => (
        <FeedbackDialogWrapper {...props}>
            <FeedbackDialogWrapper.FeedbackDialogInfoSection content={DIALOG_INFO_SECTION_CONTENT(selectedLanguage)[props.dialogKey]} />
            <FeedbackDialogWrapper.CheckboxItem name='candidateMissing' label={intl.formatMessage({ id: "candidate-did-not-show-up" })} size='medium' />
            <FeedbackDialogWrapper.FeedbackDescriptionSection id='description' name='description' label={intl.formatMessage({ id: "description" })} />
            <FeedbackDialogWrapper.RatingCriteriaWrapper>
                <FeedbackDialogWrapper.RatingCriteriaItem id='hardSkillsAssessment' name='hardSkillsAssessment' title={intl.formatMessage({ id: "hard-skills-assessment" })} />
            </FeedbackDialogWrapper.RatingCriteriaWrapper>
        </FeedbackDialogWrapper>
    ),
    candidateRatedDialog: (props) => (
        <FeedbackDialogWrapper {...props}>
            {!props.initialValues.isSoftSkillsStepCompleted || !props.initialValues.isHardSkillsStepCompleted
                ?
                <FeedbackDialogWrapper.FeedbackDialogInfoSection content={intl.formatMessage({ id: "skills-vetting-not-completed-text" })} classes={['text-red']} />
                :
                <>
                    <FeedbackDialogWrapper.RatingDetailsWrapper
                        type={intl.formatMessage({ id: "soft-skills" })}
                        tooltipContent={`${props.initialValues.softSkillsReviewer} ${moment(props.initialValues.softSkillsRateDate).format(DATE_FORMAT_WITH_DOTS)}`}
                        id='softSkillsAverageScore'
                        name='softSkillsAverageScore'
                        candidateMissingMessage={props.initialValues.softSkillsCandidateMissing ? CANDIDATE_MISSING(selectedLanguage) : null}
                        description={props.initialValues.softSkillsDescription}
                    />
                    <FeedbackDialogWrapper.RatingDetailsWrapper
                        type={intl.formatMessage({ id: "hard-skills" })}
                        tooltipContent={`${props.initialValues.hardSkillsReviewer} ${moment(props.initialValues.hardSkillsRateDate).format(DATE_FORMAT_WITH_DOTS)}`}
                        id='hardSkillsAverageScore'
                        name='hardSkillsAverageScore'
                        candidateMissingMessage={props.initialValues.hardSkillsCandidateMissing ? CANDIDATE_MISSING(selectedLanguage) : null}
                        description={props.initialValues.hardSkillsDescription}
                    />
                    <FeedbackDialogWrapper.DropdownSelect
                        label={intl.formatMessage({ id: "status" })}
                        name="status"
                        selectOptions={CANDIDATE_RATING_STATUSES_ARRAY}
                    />
                    <FeedbackDialogWrapper.DropdownSelect
                        label={intl.formatMessage({ id: "rejection-reason" })}
                        name="rejectionReason"
                        selectOptions={CANDIDATE_REJECTION_REASONS_ARRAY}
                        visibilityDependencyField="status"
                        visibilityDependencyValue={CANDIDATE_RATING_STATUSES.REJECTED}
                    />
                    <FeedbackDialogWrapper.FeedbackDescriptionSection id='internalFeedback' name='internalFeedback' label={intl.formatMessage({ id: "internal-feedback-private" })} />
                    <FeedbackDialogWrapper.FeedbackDialogInfoSection content={DIALOG_INFO_SECTION_CONTENT(selectedLanguage)[props.dialogKey]} classes={['text-red']} />
                    <FeedbackDialogWrapper.FeedbackDescriptionSection id='summary' name='summary' label={intl.formatMessage({ id: "feedback-send-to-candidate" })} />
                </>
            }
        </FeedbackDialogWrapper>
    ),
});
